import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "configureStore";
import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  fetchCompanyDetails,
  setDestroy,
} from "containers/settings/settings-container.slice";
import CompanyInfoTab from "./tabs/CompanyInfoTab";
import ApprovalRolesTab from "./tabs/ApprovalRolesTab";
import PermissionsSection from "./tabs/PermissionsSection";
import StorageSection from "./tabs/StorageSection";
import FinvaldaTab from "./tabs/FinvaldaTab";
import TerminalDeliveryTab from "./tabs/TerminalDeliveryTab";
import DokobitConnectTab from "./tabs/DokobitConnectTab";
import SignOnTabConnectTab from "./tabs/SignOnTabConnectTab";
import OnboardingTab from "./tabs/OnboardingTab";
import WorkSecurityTab from "./tabs/WorkSecurityTab";
import AssistantTab from "./tabs/AssistantTab";
import BusinessTripSettingsContainer from "./business-trip-settings-container";
import OrdersSettingsContainer from "./orders-settings-container";
import IncomingInvoicesSettings from "./incoming-invoices-settings";
import CustomerTermsSettings from "./customer-terms-settings";
import FuelReportSettingsContainer from "./fuel-report-settings-container";
import { Badge } from "./badge";
import { SettingRow, SettingsCard } from "./ui";
import { isSettingsSectionId } from "./settings-sections";

function CompanySettings() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId, section } = useParams<{
    companyId: string;
    section: string;
  }>();

  // Resolves once the company is loaded again, so a page can wait for its own save to show.
  const loadCompanyData = useCallback(async () => {
    if (companyId) {
      await dispatch(fetchCompanyDetails({ companyId }));
    }
  }, [companyId, dispatch]);

  const { companyEntity } = useSelector(
    (state: RootState) => state.companyContainerReducer
  );

  useEffect(() => {
    loadCompanyData();
    return () => {
      dispatch(setDestroy());
    };
  }, [loadCompanyData]);

  if (!isSettingsSectionId(section)) {
    return <Navigate to={`/app/${companyId}/settings/company`} replace />;
  }

  if (!companyEntity) {
    return null;
  }

  // The settings layout around this container shows the page title; each
  // section here is just its content.
  switch (section) {
    case "company":
      return (
        <CompanyInfoTab
          companyEntity={companyEntity}
          companyId={companyId || ""}
        />
      );

    case "roles":
      return (
        <ApprovalRolesTab
          companyEntity={companyEntity}
          onRefresh={loadCompanyData}
        />
      );

    case "permissions":
      return <PermissionsSection companyId={companyId || ""} />;

    case "storage":
      return (
        <StorageSection
          companyEntity={companyEntity}
          onRefresh={loadCompanyData}
        />
      );

    case "signing":
      return (
        <div className="space-y-5">
          <SettingsCard>
            <SettingRow
              label={t("settings_signing_click_title")}
              description={t("settings_signing_click_description")}
              action={
                <Badge tone="green">{t("settings_signing_always_on")}</Badge>
              }
            />
          </SettingsCard>
          <DokobitConnectTab
            companyId={companyId || ""}
            dokobit={companyEntity.dokobit}
            onRefresh={loadCompanyData}
          />
          <SignOnTabConnectTab
            companyId={companyId || ""}
            signontab={companyEntity.signontab}
            onRefresh={loadCompanyData}
          />
          <Link
            to={`/app/${companyId}/signontab-users`}
            className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50"
          >
            {t("settings_signing_tablets_link")}
            <ChevronRight className="size-4 text-gray-400" aria-hidden="true" />
          </Link>
        </div>
      );

    case "finvalda":
      return (
        <FinvaldaTab
          companyEntity={companyEntity}
          onRefresh={loadCompanyData}
        />
      );

    case "customer-terms":
      return (
        <CustomerTermsSettings
          companyEntity={companyEntity}
          onRefresh={loadCompanyData}
        />
      );

    case "sales":
      return (
        <OrdersSettingsContainer
          companyEntity={companyEntity}
          onRefresh={loadCompanyData}
        />
      );

    case "incoming-invoices":
      return <IncomingInvoicesSettings companyEntity={companyEntity} />;

    case "hr":
      return (
        <OnboardingTab
          onboardingDocumentSettings={
            companyEntity.onboardingDocumentSettings || null
          }
          assetsResponsibleUser={companyEntity.assetsResponsibleUser || null}
          onRefresh={loadCompanyData}
        />
      );

    case "procedures":
      return (
        <WorkSecurityTab
          companyEntity={companyEntity}
          onRefresh={loadCompanyData}
        />
      );

    case "business-trips":
      return (
        <BusinessTripSettingsContainer
          companyEntity={companyEntity}
          onRefresh={loadCompanyData}
        />
      );

    case "fuel":
      return (
        <FuelReportSettingsContainer
          companyEntity={companyEntity}
          onRefresh={loadCompanyData}
        />
      );

    case "terminal":
      return (
        <TerminalDeliveryTab
          companyEntity={companyEntity}
          onRefresh={loadCompanyData}
        />
      );

    case "assistant":
      return <AssistantTab />;
  }
}

export default CompanySettings;
